import React from 'react';
import styled from 'styled-components';
import { Select, DatePicker, Input, Button, Row, Col, Spin } from 'antd';
import { SearchOutlined, ClearOutlined, FolderOutlined } from '@ant-design/icons';
import type { FreePhotoFilters } from '~/stores/FreePhotoStore';
import type { Project_Base } from '~/common/typescript/models/Project';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface FreePhotoFiltersProps {
  filters: FreePhotoFilters;
  onFiltersChange: (filters: Partial<FreePhotoFilters>) => void;
  onClearFilters: () => void;
  projects: Project_Base[];
  projectsLoading?: boolean;
}

/**
 * Filter panel component for FreePhoto list
 */
export const FreePhotoFiltersComponent: React.FC<FreePhotoFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  projects,
  projectsLoading = false,
}) => {
  const handleProjectChange = (value: number | null) => {
    // If value is 0 (Tất cả) or null/undefined, clear the project filter (show all)
    if (value === 0 || value === null || value === undefined) {
      onFiltersChange({ projectId: undefined });
    } else {
      onFiltersChange({ projectId: value });
    }
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      onFiltersChange({
        dateRange: [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')],
      });
    } else {
      onFiltersChange({ dateRange: undefined });
    }
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ locationQuery: e.target.value });
  };

  const handleSortChange = (value: FreePhotoFilters['sortBy']) => {
    onFiltersChange({ sortBy: value });
  };

  return (
    <UI_Wrapper>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Select
            style={{ width: '100%' }}
            placeholder={projectsLoading ? 'Đang tải...' : 'Chọn dự án'}
            value={filters.projectId ?? 0}
            onChange={handleProjectChange}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            suffixIcon={projectsLoading ? <Spin size="small" /> : <FolderOutlined />}
            options={[
              { label: 'Tất cả dự án', value: 0 },
              ...projects.map((project) => ({
                label: project.project_name || `Project #${project.id}`,
                value: project.id,
              })),
            ]}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <RangePicker
            style={{ width: '100%' }}
            placeholder={['Từ ngày', 'Đến ngày']}
            value={filters.dateRange ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])] : null}
            onChange={handleDateRangeChange}
            format="DD/MM/YYYY"
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Tìm theo địa điểm"
            prefix={<SearchOutlined />}
            value={filters.locationQuery}
            onChange={handleLocationChange}
            allowClear
          />
        </Col>

        <Col xs={24} sm={12} md={3}>
          <Select
            style={{ width: '100%' }}
            placeholder="Sắp xếp"
            value={filters.sortBy}
            onChange={handleSortChange}
            options={[
              { label: 'Mới nhất trước', value: 'date_desc' },
              { label: 'Cũ nhất trước', value: 'date_asc' },
              { label: 'Tên A-Z', value: 'name_asc' },
              { label: 'Tên Z-A', value: 'name_desc' },
            ]}
          />
        </Col>

        <Col xs={24} sm={12} md={3}>
          <Button icon={<ClearOutlined />} onClick={onClearFilters} block>
            Xóa bộ lọc
          </Button>
        </Col>
      </Row>
    </UI_Wrapper>
  );
};

const UI_Wrapper = styled.div`
  padding: 16px 24px;
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
`;